import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
    AlertTriangle,
    Car,
    Cross,
    Droplets,
    Flame,
    Map as MapIcon,
    Mountain,
    SatelliteDish,
    Waves,
    Wind,
    Zap,
} from "lucide-react";
import "./Overview.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_API_URL_LOCAL || "";

const getStationType = (s) => s?.type || s?.station_type || "Unknown";
const getFacilityType = (h) => h?.facility_type || "Unknown";

// Trend charts span 2020 through the current year.
const CURRENT_YEAR = new Date().getFullYear();
const TREND_START_YEAR = 2020;
const TREND_YEARS = Array.from({ length: CURRENT_YEAR - TREND_START_YEAR + 1 }, (_, i) => TREND_START_YEAR + i);

// Lightning's monthly card offers 2025 onward (still includes the current
// year even if, in some future build, "now" ever moved earlier than 2025).
const LIGHTNING_YEAR_START = 2025;
const LIGHTNING_YEAR_OPTIONS = Array.from(
    { length: Math.max(1, CURRENT_YEAR - LIGHTNING_YEAR_START + 1) },
    (_, i) => LIGHTNING_YEAR_START + i
);
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Same keys as common/views.py's ACP_HAZARD_FIELDS — one color per hazard,
// reused across every ACP chart below so a hazard means the same thing
// (same color) in the bar charts, the pie chart, and the yearly chart.
const ACP_HAZARD_COLORS = {
    wind_damage: "#3B82F6",
    fire: "#EF4444",
    flood: "#06B6D4",
    landslide: "#F97316",
    storm_surge: "#8B5CF6",
    social_conflict: "#EC4899",
    other_hazard: "#6B7280",
};

// Display-only override of the backend's ACP hazard labels — backend keeps
// returning "Other Hazard" (its label catalog is otherwise fine, no reason
// to touch it for a display-text tweak), this just relabels it on render.
const ACP_LABEL_OVERRIDES = { other_hazard: "Others" };
const acpDisplayLabel = (key, fallback) => ACP_LABEL_OVERRIDES[key] || fallback;

// Metric line for hazards that combine their own channel with ACP field
// incidents - shows each side's count so the split is visible, not the sum.
const twoChannelMetric = (leftLabel, leftVal, acpCount) =>
    `${leftLabel} (${(leftVal || 0).toLocaleString()}) + ACP Incidents (${(acpCount || 0).toLocaleString()})`;

const countBy = (list, getKey) => {
    const counts = {};
    (list || []).forEach((item) => {
        const key = getKey(item) || "Unknown";
        counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
};

// manifest.json (same file HistoricalCyclone.js reads) is nested
// {year: {basin: {cycloneName: {product: [files]}}}} — every cyclone-name
// leaf, across every year/basin, is one distinct historical cyclone tracked.
const countHistoricalCyclones = (manifest) => {
    let count = 0;
    Object.values(manifest || {}).forEach((basins) => {
        Object.values(basins || {}).forEach((cyclones) => {
            count += Object.keys(cyclones || {}).length;
        });
    });
    return count;
};

const cycloneCountForYear = (manifest, year) => {
    const basins = manifest?.[String(year)];
    if (!basins) return 0;
    let count = 0;
    Object.values(basins).forEach((cyclones) => {
        count += Object.keys(cyclones || {}).length;
    });
    return count;
};

const Skeleton = ({ w = "100%", h = 14 }) => (
    <div className="ov_skeleton" style={{ width: w, height: h }} />
);

// Same plain card look as the old "Hazard Modules" link grid (ov_hazard-card/
// ov_module-icon) — one shared accent color for every icon, not a color per
// hazard, plus a total and its metric label so totals are comparable at a
// glance without pretending every hazard means the same thing
// (Cyclone/Earthquake/Lightning are recorded-event counts; Flood/Landslide/
// Fire Risk are verified felt reports + ACP field incidents combined; the
// remaining ACP cards are 2021-2025 field survey incidents with no other
// counterpart).
const StatCard = ({ icon: Icon, label, metric, total, loading }) => (
    <div className="ov_hazard-card ov_stat-card">
        <div className="ov_module-icon">
            <Icon size={19} />
        </div>
        <div>
            {loading ? <Skeleton w="40px" h="18px" /> : <div className="ov_stat-card-total">{(total || 0).toLocaleString()}</div>}
            <div className="ov_module-name">{label}</div>
            <div className="ov_stat-card-metric">{metric}</div>
        </div>
    </div>
);

// Shared column-chart look for all trend cards — categories can be years
// (Cyclone/Road Accident/Deaths) or months (Lightning). Pass either a single
// `data`+`color`+`seriesName` (plain column chart) or a `series` array of
// {name, data, color} with `stacked` (grouped-by-module chart, e.g. Deaths
// split by Road Accident/Lightning) — the legend/tooltip switch accordingly.
const TrendBarChart = ({ categories, data, color, series, seriesName, height = 240, stacked = false }) => {
    const resolvedSeries = series || [{ name: seriesName, data, color }];
    const options = {
        chart: { type: "column", backgroundColor: "transparent", height },
        title: { text: undefined },
        credits: { enabled: false },
        xAxis: {
            categories,
            labels: { style: { fontSize: "11px", color: "#6b7280" } },
            lineColor: "#e5e7eb",
            tickColor: "#e5e7eb",
        },
        yAxis: {
            title: { text: undefined },
            allowDecimals: false,
            gridLineColor: "#f0f1f3",
            labels: { style: { fontSize: "11px", color: "#6b7280" } },
        },
        legend: {
            enabled: !!series,
            itemStyle: { fontSize: "11px", fontWeight: "600", color: "#4b5563" },
        },
        tooltip: series
            ? {
                shared: true,
                headerFormat: "<b>{point.key}</b><br/>",
                pointFormat: '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y}</b><br/>',
            }
            : {
                headerFormat: "",
                pointFormat: `<b>{point.category}</b><br/>${seriesName}: <b>{point.y}</b>`,
            },
        plotOptions: {
            column: {
                borderRadius: stacked ? 0 : 4,
                borderWidth: 0,
                pointPadding: 0.12,
                groupPadding: 0.08,
                stacking: stacked ? "normal" : undefined,
            },
        },
        series: resolvedSeries,
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
};

const defaultAcpColor = (h) => ACP_HAZARD_COLORS[h.key];

// Reused for both the ACP-only "Incidents by Hazard Type" pie (valueKey
// defaults to incident_count, color from ACP_HAZARD_COLORS by key) and the
// combined all-hazard pie (valueKey="total", color taken directly from each
// hazard's own `color` field via getColor, since that list mixes ACP and
// non-ACP hazards under one shared set of keys).
const HazardPieChart = ({ hazards, valueKey = "incident_count", getColor = defaultAcpColor, valueLabel = "incidents", height = 240 }) => {
    // A 0-value slice has zero angular width and contributes nothing to
    // "share of total" — dropped so it doesn't clutter the legend either.
    const nonZero = hazards.filter((h) => h[valueKey] > 0);
    const options = {
        chart: { type: "pie", backgroundColor: "transparent", height, spacing: [10, 10, 10, 10] },
        title: { text: undefined },
        credits: { enabled: false },
        tooltip: { pointFormat: `<b>{point.y}</b> ${valueLabel} ({point.percentage:.1f}%)` },
        plotOptions: {
            pie: {
                size: "30%",
                dataLabels: {
                    enabled: true,
                    format: "<b>{point.name}: {point.y}</b>",
                    style: { fontSize: "10px", fontWeight: "bold", color: "#111827", whiteSpace: "nowrap", textOverflow: "none" },
                    // allowOverlap:true is the one setting that actually
                    // guarantees Highcharts renders every label — with
                    // allowOverlap:false its distribute() collision layout can
                    // end up not rendering one or two labels at all when slice
                    // sizes are this uneven (confirmed: that's what was hiding
                    // Landslide/Other Hazard even with generous distance/crop
                    // tuning). The tradeoff is that two labels can sit close
                    // together for adjacent small slices, but every one of
                    // them is always on screen, which is the actual ask.
                    allowOverlap: true,
                    crop: false,
                    distance: 22,
                    connectorShape: "straight",
                    connectorWidth: 1.5,
                },
                showInLegend: false,
            },
        },
        legend: { enabled: false },
        series: [{
            name: valueLabel,
            colorByPoint: true,
            data: nonZero.map((h) => ({ name: h.label, y: h[valueKey], color: getColor(h) })),
        }],
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
};

const Overview = () => {
    const [loading, setLoading] = useState(true);
    const [stations, setStations] = useState([]);
    const [hospitals, setHospitals] = useState([]);

    const [impactLoading, setImpactLoading] = useState(true);
    const [cycloneManifest, setCycloneManifest] = useState(null);
    const [roadAccidentYearlyRaw, setRoadAccidentYearlyRaw] = useState([]);

    const [lightningYear, setLightningYear] = useState(CURRENT_YEAR);
    const [lightningMonthly, setLightningMonthly] = useState([]);
    const [lightningLoading, setLightningLoading] = useState(true);
    // 'total' = every observation; 'timor' = only ones resolved to a
    // Timor-Leste admin unit (lightning/monthly-stats/'s timor_total).
    const [lightningView, setLightningView] = useState("total");

    // ACP field-collected incident reports (common/acp-hazard-totals/,
    // common/acp-hazard-yearly/) — 'count' = incidents, 'impact' = families
    // affected, kept as a toggle rather than one dual-axis chart since the
    // two metrics rank hazards differently.
    const [acpTotals, setAcpTotals] = useState([]);
    const [acpYears, setAcpYears] = useState([]);
    const [acpYearlyHazards, setAcpYearlyHazards] = useState([]);
    const [acpLoading, setAcpLoading] = useState(true);

    // Real per-year event/observation counts (Earthquake/yearly-count/,
    // lightning/yearly-count/) and verified-felt-report-only counts (Flood/
    // Landslide/Fire Risk have no authoritative event log — see each
    // endpoint's own docstring) — for the "like Cyclone" yearly charts and
    // the unified stat cards.
    const [earthquakeYearly, setEarthquakeYearly] = useState({ years: [], total_counts: [] });
    const [lightningYearlyAllTime, setLightningYearlyAllTime] = useState({ years: [], counts: [] });
    const [floodFeltYearly, setFloodFeltYearly] = useState({ years: [], counts: [] });
    const [landslideFeltYearly, setLandslideFeltYearly] = useState({ years: [], counts: [] });
    const [fireriskFeltYearly, setFireriskFeltYearly] = useState({ years: [], counts: [] });
    const [hazardStatsLoading, setHazardStatsLoading] = useState(true);

    const fetchInfrastructure = useCallback(async () => {
        const [stationsRes, hospitalsRes] = await Promise.allSettled([
            fetch(`${BACKEND_URL}cyclone/station-all/`).then((r) => (r.ok ? r.json() : [])),
            fetch(`${BACKEND_URL}roadaccident/get_hospital_details/`).then((r) => (r.ok ? r.json() : [])),
        ]);

        if (stationsRes.status === "fulfilled") {
            setStations(Array.isArray(stationsRes.value) ? stationsRes.value : []);
        }
        if (hospitalsRes.status === "fulfilled") {
            setHospitals(Array.isArray(hospitalsRes.value) ? hospitalsRes.value : []);
        }
        setLoading(false);
    }, []);

    // Historical cyclone manifest (static, same source HistoricalCyclone.js
    // reads — gives both the total and the per-year breakdown) and road
    // accident yearly totals (roadaccident/get_yearly_deathdata/ — already
    // grouped by year server-side).
    const fetchImpactSummary = useCallback(async () => {
        const [manifestRes, roadAccidentRes] = await Promise.allSettled([
            fetch("/DATA/Cyclone/TSR/Historical/manifest.json").then((r) => (r.ok ? r.json() : {})),
            fetch(`${BACKEND_URL}roadaccident/get_yearly_deathdata/`).then((r) => (r.ok ? r.json() : [])),
        ]);

        if (manifestRes.status === "fulfilled") {
            setCycloneManifest(manifestRes.value || {});
        }
        if (roadAccidentRes.status === "fulfilled") {
            setRoadAccidentYearlyRaw(Array.isArray(roadAccidentRes.value) ? roadAccidentRes.value : []);
        }
        setImpactLoading(false);
    }, []);

    // Server-aggregated month buckets for the selected year — re-fetched
    // every time the dropdown changes (see lightning/monthly-stats/, added
    // specifically for this card so a year's worth of individual strikes
    // never has to be pulled/aggregated client-side).
    const fetchLightningMonthly = useCallback(async (year) => {
        setLightningLoading(true);
        try {
            const res = await fetch(`${BACKEND_URL}lightning/monthly-stats/?year=${year}`);
            const data = res.ok ? await res.json() : { months: [] };
            setLightningMonthly(Array.isArray(data.months) ? data.months : []);
        } catch (e) {
            setLightningMonthly([]);
        }
        setLightningLoading(false);
    }, []);

    const fetchAcpHazardStats = useCallback(async () => {
        const [totalsRes, yearlyRes] = await Promise.allSettled([
            fetch(`${BACKEND_URL}common/acp-hazard-totals/`).then((r) => (r.ok ? r.json() : { hazards: [] })),
            fetch(`${BACKEND_URL}common/acp-hazard-yearly/`).then((r) => (r.ok ? r.json() : { years: [], hazards: [] })),
        ]);

        if (totalsRes.status === "fulfilled") {
            setAcpTotals(totalsRes.value?.hazards || []);
        }
        if (yearlyRes.status === "fulfilled") {
            setAcpYears(yearlyRes.value?.years || []);
            setAcpYearlyHazards(yearlyRes.value?.hazards || []);
        }
        setAcpLoading(false);
    }, []);

    const fetchHazardYearlyStats = useCallback(async () => {
        const [eqRes, ltRes, flRes, lsRes, frRes] = await Promise.allSettled([
            fetch(`${BACKEND_URL}EarthQuake/yearly-count/`).then((r) => (r.ok ? r.json() : { years: [], total_counts: [] })),
            fetch(`${BACKEND_URL}lightning/yearly-count/`).then((r) => (r.ok ? r.json() : { years: [], counts: [] })),
            fetch(`${BACKEND_URL}flood/felt-report-yearly-count/`).then((r) => (r.ok ? r.json() : { years: [], counts: [] })),
            fetch(`${BACKEND_URL}landslide/felt-report-yearly-count/`).then((r) => (r.ok ? r.json() : { years: [], counts: [] })),
            fetch(`${BACKEND_URL}firerisk/felt-report-yearly-count/`).then((r) => (r.ok ? r.json() : { years: [], counts: [] })),
        ]);

        if (eqRes.status === "fulfilled") setEarthquakeYearly(eqRes.value || { years: [], total_counts: [] });
        if (ltRes.status === "fulfilled") setLightningYearlyAllTime(ltRes.value || { years: [], counts: [] });
        if (flRes.status === "fulfilled") setFloodFeltYearly(flRes.value || { years: [], counts: [] });
        if (lsRes.status === "fulfilled") setLandslideFeltYearly(lsRes.value || { years: [], counts: [] });
        if (frRes.status === "fulfilled") setFireriskFeltYearly(frRes.value || { years: [], counts: [] });
        setHazardStatsLoading(false);
    }, []);

    useEffect(() => {
        fetchInfrastructure();
        fetchImpactSummary();
        fetchAcpHazardStats();
        fetchHazardYearlyStats();
    }, [fetchInfrastructure, fetchImpactSummary, fetchAcpHazardStats, fetchHazardYearlyStats]);

    useEffect(() => {
        fetchLightningMonthly(lightningYear);
    }, [lightningYear, fetchLightningMonthly]);

    const stationTypeCounts = countBy(stations, getStationType);
    const hospitalTypeCounts = countBy(hospitals, getFacilityType);

    const cycloneCount = useMemo(() => countHistoricalCyclones(cycloneManifest), [cycloneManifest]);
    const cycloneYearlySeries = useMemo(
        () => TREND_YEARS.map((year) => cycloneCountForYear(cycloneManifest, year)),
        [cycloneManifest]
    );

    const roadAccidentByYear = useMemo(() => {
        const map = {};
        roadAccidentYearlyRaw.forEach((r) => { map[r.year] = r.total_accidents || 0; });
        return map;
    }, [roadAccidentYearlyRaw]);
    const roadAccidentYearlySeries = useMemo(
        () => TREND_YEARS.map((year) => roadAccidentByYear[year] || 0),
        [roadAccidentByYear]
    );
    // Records-channel yearly as {years, counts}, so it can be merged with
    // ACP road_accident the same way Flood/Landslide/Fire Risk are.
    const roadAccidentYearly = useMemo(() => ({
        years: roadAccidentYearlyRaw.map((r) => r.year),
        counts: roadAccidentYearlyRaw.map((r) => r.total_accidents || 0),
    }), [roadAccidentYearlyRaw]);
    const roadAccidentTotal = useMemo(
        () => roadAccidentYearlySeries.reduce((sum, v) => sum + v, 0),
        [roadAccidentYearlySeries]
    );

    const sumArray = (arr) => (arr || []).reduce((sum, v) => sum + (v || 0), 0);
    const earthquakeTotal = useMemo(() => sumArray(earthquakeYearly.total_counts), [earthquakeYearly]);
    const lightningAllTimeTotal = useMemo(() => sumArray(lightningYearlyAllTime.counts), [lightningYearlyAllTime]);
    const floodFeltTotal = useMemo(() => sumArray(floodFeltYearly.counts), [floodFeltYearly]);
    const landslideFeltTotal = useMemo(() => sumArray(landslideFeltYearly.counts), [landslideFeltYearly]);
    const fireriskFeltTotal = useMemo(() => sumArray(fireriskFeltYearly.counts), [fireriskFeltYearly]);

    const acpByKey = useMemo(() => {
        const map = {};
        acpTotals.forEach((h) => { map[h.key] = h; });
        return map;
    }, [acpTotals]);
    const acpYearlyByKey = useMemo(() => {
        const map = {};
        acpYearlyHazards.forEach((h) => { map[h.key] = h; });
        return map;
    }, [acpYearlyHazards]);

    // Union of every year that appears anywhere, so one x-axis can carry
    // every hazard's series without dropping years some sources don't have.
    const combinedYears = useMemo(() => {
        const set = new Set([
            ...TREND_YEARS,
            ...(earthquakeYearly.years || []),
            ...(lightningYearlyAllTime.years || []),
            ...acpYears,
        ]);
        return Array.from(set).sort((a, b) => a - b);
    }, [earthquakeYearly, lightningYearlyAllTime, acpYears]);

    const alignToYears = useCallback((years, srcYears, srcCounts) => {
        const map = {};
        (srcYears || []).forEach((y, i) => { map[y] = (srcCounts || [])[i] || 0; });
        return years.map((y) => map[y] || 0);
    }, []);

    // Flood/Landslide/Fire Risk: verified felt reports are currently all
    // still pending (0 verified across the board, checked directly against
    // the live DB), so shown alone they read as "nothing happened" even
    // though ACP's 2021-2025 field survey has real counts for these same
    // hazard types. Combining the two channels into one final count/series
    // is what the felt-report pipeline should eventually catch up to once
    // reports get verified — until then this is the honest fuller picture.
    const mergeFeltAndAcp = useCallback((feltYearly, acpKey) => {
        const acpSeries = acpYearlyByKey[acpKey];
        const feltAligned = alignToYears(combinedYears, feltYearly.years, feltYearly.counts);
        const acpAligned = alignToYears(combinedYears, acpYears, acpSeries?.counts || []);
        return combinedYears.map((_, i) => feltAligned[i] + acpAligned[i]);
    }, [acpYearlyByKey, combinedYears, acpYears, alignToYears]);
    const floodCombinedSeries = useMemo(() => mergeFeltAndAcp(floodFeltYearly, "flood"), [floodFeltYearly, mergeFeltAndAcp]);
    const landslideCombinedSeries = useMemo(() => mergeFeltAndAcp(landslideFeltYearly, "landslide"), [landslideFeltYearly, mergeFeltAndAcp]);
    const fireriskCombinedSeries = useMemo(() => mergeFeltAndAcp(fireriskFeltYearly, "fire"), [fireriskFeltYearly, mergeFeltAndAcp]);
    const roadAccidentCombinedSeries = useMemo(() => mergeFeltAndAcp(roadAccidentYearly, "road_accident"), [roadAccidentYearly, mergeFeltAndAcp]);

    const floodFinalTotal = useMemo(() => floodFeltTotal + (acpByKey.flood?.incident_count || 0), [floodFeltTotal, acpByKey]);
    const landslideFinalTotal = useMemo(() => landslideFeltTotal + (acpByKey.landslide?.incident_count || 0), [landslideFeltTotal, acpByKey]);
    const fireriskFinalTotal = useMemo(() => fireriskFeltTotal + (acpByKey.fire?.incident_count || 0), [fireriskFeltTotal, acpByKey]);
    const roadAccidentAcpCount = useMemo(() => acpByKey.road_accident?.incident_count || 0, [acpByKey]);
    const roadAccidentFinalTotal = useMemo(() => roadAccidentTotal + roadAccidentAcpCount, [roadAccidentTotal, roadAccidentAcpCount]);

    // One combined list driving both the stat-card row and the pie chart —
    // every hazard the app covers, each carrying its own honest metric
    // label. Flood/Landslide/Fire Risk show the merged felt-report+ACP
    // total above; the remaining ACP-only hazards (no felt-report
    // counterpart) keep their own cards.
    const heroHazardTotals = useMemo(() => {
        const acpOnlyKeys = ["wind_damage", "storm_surge", "social_conflict", "other_hazard"];
        const acpIcons = { wind_damage: Wind, storm_surge: Waves, social_conflict: AlertTriangle, other_hazard: AlertTriangle };
        const acpCards = acpOnlyKeys
            .filter((key) => acpByKey[key])
            .map((key) => ({
                key: `acp_${key}`,
                label: acpDisplayLabel(key, acpByKey[key].label),
                metric: "ACP Field Incidents",
                total: acpByKey[key].incident_count,
                color: ACP_HAZARD_COLORS[key],
                icon: acpIcons[key] || AlertTriangle,
            }));
        // Road Accident sits just to the left of "Others" (other_hazard),
        // wherever that ends up in acpCards, rather than a fixed index -
        // acpCards only includes whichever ACP-only hazards actually have
        // data, so "Others" isn't guaranteed to be last.
        const roadAccidentCard = {
            key: "road_accident",
            label: "Road Accident",
            metric: twoChannelMetric("Recorded Accidents", roadAccidentTotal, roadAccidentAcpCount),
            total: roadAccidentFinalTotal,
            color: "#EF4444",
            icon: Car,
        };
        const acpCardsWithRoadAccident = [...acpCards];
        const othersIndex = acpCardsWithRoadAccident.findIndex((c) => c.key === "acp_other_hazard");
        if (othersIndex === -1) {
            acpCardsWithRoadAccident.push(roadAccidentCard);
        } else {
            acpCardsWithRoadAccident.splice(othersIndex, 0, roadAccidentCard);
        }

        return [
            { key: "cyclone", label: "Cyclone", metric: "Historical Tracks Recorded", total: cycloneCount, color: "#3B82F6", icon: Wind },
            { key: "earthquake", label: "Earthquake", metric: "Recorded Events, Timor Region", total: earthquakeTotal, color: "#F59E0B", icon: Waves },
            { key: "lightning", label: "Lightning", metric: "Detected Observations", total: lightningAllTimeTotal, color: "#8B5CF6", icon: Zap },
            { key: "flood_final", label: "Flood", metric: twoChannelMetric("Verified Felt Reports", floodFeltTotal, acpByKey.flood?.incident_count), total: floodFinalTotal, color: "#06B6D4", icon: Droplets },
            { key: "landslide_final", label: "Landslide", metric: twoChannelMetric("Verified Felt Reports", landslideFeltTotal, acpByKey.landslide?.incident_count), total: landslideFinalTotal, color: "#F97316", icon: Mountain },
            { key: "firerisk_final", label: "Fire Risk", metric: twoChannelMetric("Verified Felt Reports", fireriskFeltTotal, acpByKey.fire?.incident_count), total: fireriskFinalTotal, color: "#DC2626", icon: Flame },
            ...acpCardsWithRoadAccident,
        ];
    }, [cycloneCount, roadAccidentTotal, roadAccidentAcpCount, roadAccidentFinalTotal, earthquakeTotal, lightningAllTimeTotal,
        floodFinalTotal, landslideFinalTotal, fireriskFinalTotal,
        floodFeltTotal, landslideFeltTotal, fireriskFeltTotal, acpByKey]);

    const heroTotalAllHazards = useMemo(
        () => heroHazardTotals.reduce((sum, h) => sum + (h.total || 0), 0),
        [heroHazardTotals]
    );

    // The one combined "Hazards by Year" chart — every hazard's event/report
    // count on a shared year axis, toggleable via the legend. Kept to a
    // single shared metric (counts) rather than also offering ACP's
    // families-affected figure here, since that second metric only exists
    // for the ACP-native hazards and mixing it into a chart most series
    // don't have would be misleading.
    const combinedYearlySeries = useMemo(() => {
        const acpOnlySeries = ["wind_damage", "storm_surge", "social_conflict", "other_hazard"]
            .filter((key) => acpYearlyByKey[key])
            .map((key) => ({
                name: acpDisplayLabel(key, acpYearlyByKey[key].label),
                data: alignToYears(combinedYears, acpYears, acpYearlyByKey[key].counts),
                color: ACP_HAZARD_COLORS[key],
            }));
        return [
            { name: "Cyclone", data: alignToYears(combinedYears, TREND_YEARS, cycloneYearlySeries), color: "#3B82F6" },
            { name: "Road Accident", data: roadAccidentCombinedSeries, color: "#EF4444" },
            { name: "Earthquake", data: alignToYears(combinedYears, earthquakeYearly.years, earthquakeYearly.total_counts), color: "#F59E0B" },
            { name: "Lightning", data: alignToYears(combinedYears, lightningYearlyAllTime.years, lightningYearlyAllTime.counts), color: "#8B5CF6" },
            { name: "Flood", data: floodCombinedSeries, color: "#06B6D4" },
            { name: "Landslide", data: landslideCombinedSeries, color: "#F97316" },
            { name: "Fire Risk", data: fireriskCombinedSeries, color: "#DC2626" },
            ...acpOnlySeries,
        ];
    }, [combinedYears, cycloneYearlySeries, roadAccidentCombinedSeries, earthquakeYearly, lightningYearlyAllTime, floodCombinedSeries, landslideCombinedSeries, fireriskCombinedSeries, acpYearlyByKey, acpYears, alignToYears]);

    const lightningTotalKey = lightningView === "timor" ? "timor_total" : "total";
    const lightningMonthlySeries = useMemo(
        () => lightningMonthly.map((m) => m[lightningTotalKey] || 0),
        [lightningMonthly, lightningTotalKey]
    );
    const lightningYearTotal = useMemo(
        () => lightningMonthlySeries.reduce((sum, v) => sum + v, 0),
        [lightningMonthlySeries]
    );

    return (
        <div className="ov_page">
            {/* Stations & exposure infrastructure */}
            <section className="ov_section">
                <div className="ov_section-header">
                    <h2>Stations &amp; Exposure Infrastructure</h2>
                </div>
                <div className="ov_infra-grid">
                    <div className="ov_infra-card">
                        <div className="ov_infra-head">
                            <div className="ov_infra-icon">
                                <SatelliteDish size={18} />
                            </div>
                            <div>
                                {loading ? <Skeleton w="30px" h="20px" /> : <div className="ov_infra-total">{stations.length}</div>}
                                <div className="ov_infra-title">Weather Stations</div>
                            </div>
                        </div>
                        <div className="ov_infra-breakdown">
                            {Object.entries(stationTypeCounts).length === 0 && !loading && (
                                <div className="ov_infra-row">No station data available</div>
                            )}
                            {Object.entries(stationTypeCounts).map(([type, c]) => (
                                <div className="ov_infra-row" key={type}>
                                    <span>{type}</span>
                                    <b>{c}</b>
                                </div>
                            ))}
                        </div>
                        <Link to="/WeatherStation" className="ov_infra-link">
                            View Weather Stations
                        </Link>
                    </div>

                    <div className="ov_infra-card">
                        <div className="ov_infra-head">
                            <div className="ov_infra-icon">
                                <Cross size={18} />
                            </div>
                            <div>
                                {loading ? <Skeleton w="30px" h="20px" /> : <div className="ov_infra-total">{hospitals.length}</div>}
                                <div className="ov_infra-title">Health Facilities</div>
                            </div>
                        </div>
                        <div className="ov_infra-breakdown">
                            {Object.entries(hospitalTypeCounts).length === 0 && !loading && (
                                <div className="ov_infra-row">No facility data available</div>
                            )}
                            {Object.entries(hospitalTypeCounts).map(([type, c]) => (
                                <div className="ov_infra-row" key={type}>
                                    <span>{type}</span>
                                    <b>{c}</b>
                                </div>
                            ))}
                        </div>
                        <Link to="/Roadaccident/Hospitals" className="ov_infra-link">
                            View Health Facilities
                        </Link>
                    </div>

                    <div className="ov_infra-card">
                        <div className="ov_infra-head">
                            <div className="ov_infra-icon">
                                <MapIcon size={18} />
                            </div>
                            <div>
                                <div className="ov_infra-total">14</div>
                                <div className="ov_infra-title">Municipalities Covered</div>
                            </div>
                        </div>
                        <div className="ov_infra-breakdown">
                            <div className="ov_infra-row"><span>Sub-Municipalities</span><b>81</b></div>
                            <div className="ov_infra-row"><span>Sucos</span><b>562</b></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Hazard Statistics: every hazard's total-count card first,
                then the combined pie + yearly chart, then supplementary
                detail views. */}
            <section className="ov_section">
                <div className="ov_section-header">
                    <h2>Hazard Statistics</h2>
                </div>

                {/* 1. Cards + total counts */}
                <div className="ov_hazard-grid">
                    {heroHazardTotals.map((h) => (
                        <StatCard key={h.key} icon={h.icon} label={h.label} metric={h.metric} total={h.total} loading={impactLoading || acpLoading || hazardStatsLoading} />
                    ))}
                </div>

                {/* 2. Pie chart (left) + combined "Hazards by Year" (right) */}
                <div className="ov_two-col-row">
                    <div className="ov_chart-card ov_two-col-left">
                        <div className="ov_chart-card-head">
                            <div className="ov_chart-card-meta">
                                <div>
                                    {(impactLoading || acpLoading || hazardStatsLoading) ? <Skeleton w="30px" h="20px" /> : <div className="ov_infra-total">{heroTotalAllHazards.toLocaleString()}</div>}
                                    <div className="ov_infra-title">Share of Total Across All Hazards</div>
                                </div>
                            </div>
                        </div>
                        {(impactLoading || acpLoading || hazardStatsLoading) ? (
                            <Skeleton h="380px" />
                        ) : (
                            <HazardPieChart hazards={heroHazardTotals} valueKey="total" getColor={(h) => h.color} valueLabel="total" height={380} />
                        )}
                    </div>

                    <div className="ov_chart-card ov_two-col-right">
                        <div className="ov_chart-card-head">
                            <div className="ov_chart-card-meta">
                                <div className="ov_infra-title">Hazards by Year</div>
                            </div>
                        </div>
                        <p className="ov_chart-card-note">
                            Every hazard's event/report count, combined on one year axis — click a legend entry to isolate it.
                        </p>
                        {(impactLoading || acpLoading || hazardStatsLoading) ? (
                            <Skeleton h="380px" />
                        ) : (
                            <TrendBarChart categories={combinedYears} series={combinedYearlySeries} height={380} />
                        )}
                    </div>
                </div>

                {/* 3. Supplementary detail views that don't fit the combined chart above:
                    the standalone Cyclone card gives its own dedicated
                    yearly chart (same treatment it always had); Lightning's
                    month-level breakdown is finer-grained than a yearly chart. */}
                <div className="ov_chart-grid">
                    <div className="ov_chart-card">
                        <div className="ov_chart-card-head">
                            <div className="ov_chart-card-meta">
                                <div className="ov_infra-icon">
                                    <Wind size={18} />
                                </div>
                                <div>
                                    {impactLoading ? <Skeleton w="30px" h="20px" /> : <div className="ov_infra-total">{cycloneCount}</div>}
                                    <div className="ov_infra-title">Historical Cyclones Tracked</div>
                                </div>
                            </div>
                        </div>
                        {impactLoading ? (
                            <Skeleton h="240px" />
                        ) : (
                            <TrendBarChart categories={TREND_YEARS} data={cycloneYearlySeries} color="#3B82F6" seriesName="Cyclones" />
                        )}
                        <Link to="/Cyclone/HistoricalCyclone" className="ov_chart-card-link">
                            View Historical Cyclones
                        </Link>
                    </div>

                    <div className="ov_chart-card ov_chart-card-wide">
                        <div className="ov_chart-card-head">
                            <div className="ov_chart-card-meta">
                                <div className="ov_infra-icon">
                                    <Zap size={18} />
                                </div>
                                <div>
                                    {lightningLoading ? <Skeleton w="30px" h="20px" /> : <div className="ov_infra-total">{lightningYearTotal}</div>}
                                    <div className="ov_infra-title">Lightning Observations — {lightningYear}</div>
                                </div>
                            </div>
                            <div className="ov_chart-card-controls">
                                <div className="ov_chart-toggle" role="group" aria-label="Total vs Timor Region">
                                    <button
                                        type="button"
                                        className={lightningView === "total" ? "active" : ""}
                                        onClick={() => setLightningView("total")}
                                    >
                                        Total
                                    </button>
                                    <button
                                        type="button"
                                        className={lightningView === "timor" ? "active" : ""}
                                        onClick={() => setLightningView("timor")}
                                    >
                                        Timor Region
                                    </button>
                                </div>
                                <select
                                    className="ov_chart-year-select"
                                    value={lightningYear}
                                    onChange={(e) => setLightningYear(Number(e.target.value))}
                                    aria-label="Select year"
                                >
                                    {LIGHTNING_YEAR_OPTIONS.map((year) => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {lightningLoading ? (
                            <Skeleton h="240px" />
                        ) : (
                            <TrendBarChart
                                categories={MONTH_LABELS}
                                data={lightningMonthlySeries}
                                color="#8B5CF6"
                                seriesName={lightningView === "timor" ? "Observations (Timor Region)" : "Observations (Total)"}
                            />
                        )}
                        <Link to="/Lightning/Nowcast" className="ov_chart-card-link">
                            View Lightning Nowcast
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Overview;
